import _ from "lodash";
import React from "react";
import classNames from "classnames";
import get from "lodash/get";
import startCase from "lodash/startCase";
import withStyles from "elevate-ui/withStyles";

import DisclaimerSold from "./DisclaimerSold";
import { withGlobalContext } from "../common/GlobalContext";
import { withListingContext } from "./ListingContext";
import Accordion from "elevate-ui/Accordion";

const StandardValue = ({ classes, label, value, path }) => {
  const strippedValue =
    value !== true && !value.includes("$") && !value.match(/^[0-9,]*$/gm)
      ? value.split(",").join(", ")
      : value;

  return (
    <div className={classNames(classes.root, `ref__${path}`)}>
      <div className={classes.label}>{label}</div>
      <div className={classes.value}>
        {value === true ? "Yes" : strippedValue.split("  ").join(" ")}
      </div>
    </div>
  );
};

const MoneyValue = ({ classes, label, value, path }) => {
  const updatedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
  return (
    <div className={classNames(classes.root, `ref__${path}`)}>
      <div className={classes.label}>{label}</div>
      <div className={classes.value}>{updatedValue}</div>
    </div>
  );
};

const Detail = withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: "10px 4px 10px 16px",

    "&:nth-child(odd)": {
      backgroundColor: "#fafafa",
    },

    [theme.breakpoints(600)]: {
      flexDirection: "row",
    },
  },
  label: {
    fontSize: "16px",
    lineHeight: 1.2,
    fontWeight: "600",
    color: theme.colors.gray["900"],
    width: "216px",
    flexShrink: 0,
  },
  value: {
    fontSize: "16px",
    lineHeight: 1.2,
    fontWeight: "400",
    color: theme.colors.gray["900"],
    marginTop: theme.spacing.unit * 0.5,

    [theme.breakpoints(600)]: {
      marginTop: "0",
      marginLeft: theme.spacing.unit * 0.5,
    },
  },
}))(({ classes, label, listing, path, value }) => {
  if (_.isNil(value)) {
    return null;
  }
  if (value === false && label === "Sub-Agency Relationship Offered") {
    return (
      <StandardValue classes={classes} label={label} value="No" path={path} />
    );
  }
  if (!value) {
    return null;
  }

  if (!listing.hasAssociation && (label === "HOA" || label === "HOA Fees")) {
    return null;
  }

  if (label === "HOA Fees" && value > 0) {
    return (
      <MoneyValue classes={classes} label={label} value={value} path={path} />
    );
  } else if (label === "HOA Fees" && value <= 0) {
    return null;
  }

  if (label === "Schools") {
    const schoolValue = listing.schools
      ? listing.schools
          .map((school) => {
            if (school.name) {
              return `${startCase(school.type)} School: ${school.name || ""}`;
            }
            return null;
          })
          .join(" | ")
      : " - ";
    return (
      <StandardValue
        classes={classes}
        label={label}
        value={schoolValue}
        path={path}
      />
    );
  }

  return (
    <StandardValue classes={classes} label={label} value={value} path={path} />
  );
});

const Section = withStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
}))(({ category, classes, compliance, listing, additionalFields }) => (
  <div className={classes.root}>
    {additionalFields[category].map((field) => {
      // COMPLIANCE: Triad homes must show "acres" instead of "lot size"
      if (
        compliance &&
        compliance.listingResults &&
        compliance.listingResults.showAcresInsteadOfLotSize
      ) {
        if (field.path === "squareFootageFormatted") return null; // hide/skip "lot size" for triad
      }
      return (
        <Detail
          key={field.path}
          listing={listing}
          label={field.label}
          path={field.path}
          value={field.value || get(listing, field.path)}
        />
      );
    })}
  </div>
));

const complianceFieldsTransformer = (
  compliance,
  additionalFields,
  additionalDetailsLayout
) => {
  if (compliance && compliance.listingInsertions) {
    compliance.listingInsertions.forEach((item) => {
      if (!additionalFields[item.category]) {
        additionalFields[item.category] = [];
        additionalDetailsLayout.push({
          title: item.category,
          name: item.category,
        });
      }
      if (
        additionalFields[item.category] &&
        additionalFields[item.category].filter((items) => {
          return items.path === item.path;
        }).length === 0
      ) {
        additionalFields[item.category].push({
          path: item.path,
          label: item.label,
        });
      }
    });
  }

  return { additionalFields, additionalDetailsLayout };
};

const AdditionalDetails = ({
  classes,
  className,
  compliance,
  listing,
  openIndexes,
  additionalDetailsLayout,
  additionalFields,
}) => {
  const {
    additionalFields: additionalFieldsWithCompliance,
    additionalDetailsLayout: additionalDetailsLayoutWithCompliance,
  } = complianceFieldsTransformer(
    compliance,
    additionalFields,
    additionalDetailsLayout
  );

  const items = _.map(additionalDetailsLayoutWithCompliance, (section) => {
    return {
      title: section.title,
      contents: (
        <Section
          category={section.name}
          listing={listing}
          compliance={compliance}
          additionalFields={additionalFieldsWithCompliance}
        />
      ),
    };
  });

  return (
    <div className={classNames(classes.root, className)}>
      <Accordion
        classes={{
          button: classes.accordionButton,
          headerTitle: classes.title,
          content: classes.content,
        }}
        items={items}
        openIndexes={openIndexes}
        style={{ marginTop: "16px" }}
      />
      {listing.status === "Sold" && <DisclaimerSold />}
    </div>
  );
};

export default withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  accordionButton: {
    display: "flex",
    padding: "12px 16px",
    textAlign: "left",
    alignItems: "center",
  },
  title: {
    display: "flex",
    fontWeight: "600",
    fontSize: "15px",
    textTransform: "uppercase",
    color: theme.colors.gray["700"],
  },
  content: {
    display: "flex",
    flexDirection: "column",
  },
}))(withGlobalContext(withListingContext(AdditionalDetails)));
